import { Injectable } from '@angular/core';
import {
  BzApteka, BzProduct,
  IProductRow,
  IRequestGoodsModel,
  IReserveOrder,
  IUpdateOrder,
  IUserOrder
} from '@interfaces';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  AddOrdersToHistory,
  CancelOneOrderAndArchiveAction,
  SetOrderPharmaciesAction,
  UpdateOrderAction
} from '../../../store/actions';
import { LocalStorageService } from '@core/services';
import { Store } from '@ngrx/store';
import { AppState } from '@appStates';
import { RestUrlService, TypeUrl } from './settings/rest-url.service';
import { filter, map, switchMap } from 'rxjs/operators';
import { getMinTimestampForCurrentOrdersReq } from 'src/app/shared/utils';

@Injectable({
  providedIn: 'root'
})
export class OrdersApiService {
  private readonly _url: string;

  constructor(
    private readonly http: HttpClient,
    private readonly localStorageService: LocalStorageService,
    private readonly store: Store<AppState>,
    private readonly urlRest: RestUrlService
  ) {
    this._url = 'https:' + this.urlRest.getUrl(TypeUrl.rest);
  }

  private static orderedGoods(apteka: BzApteka[]): IRequestGoodsModel[] {
    return apteka.map((item: BzApteka) => {
      return {
        pharmacyId: item.id,
        region: item.region,
        products: item.products.map((product: BzProduct) => {
          return {
            productId: product.id,
            price: product.price,
            priceSingle: product.priceSingle,
            priceBlister: product.priceBlister,
            packageNum: product.packageNum,
            blisterNum: product.blisterNum
          };
        })
      };
    });
  }

  public reserveOrder(list: BzApteka[], user: IUserOrder): Observable<IReserveOrder> {
    const pharmacies: IRequestGoodsModel[] = OrdersApiService.orderedGoods(list);
    const order = Object.assign({ pharmacies }, user);
    return this.http.post<IReserveOrder>(this._url + '/api/orders/new', order);
  }

  public getCompletedOrders(pageNum: number): void {
    const firstIndex: number = (pageNum * 10) - 10;
    const lastIndex: number = (pageNum * 10);

    this.localStorageService.getHistoryOrders().pipe(
      map((historyItems) => historyItems.filter(({ timestamp }) => timestamp < getMinTimestampForCurrentOrdersReq() || !timestamp)),
      filter((items) => items.length > 0),
      map((array) => array.slice(firstIndex, lastIndex)),
      switchMap((elements) => this.http.post(this._url + '/api/v2/orders/archive', elements.map((el) => el.id)))
    ).subscribe((orders: BzApteka[]) => {
      this.store.dispatch(AddOrdersToHistory({ data: orders }));
    });
  }

  public getCurrentOrders(pageNum: number): void {
    const firstIndex: number = (pageNum * 10) - 10;
    const lastIndex: number = (pageNum * 10);

    this.localStorageService.getHistoryOrders().pipe(
      map((historyItems) => historyItems.filter(({ timestamp }) => timestamp && timestamp >= getMinTimestampForCurrentOrdersReq())),
      filter((items) => items.length > 0),
      map((array) => array.slice(firstIndex, lastIndex)),
      filter((orders) => orders.length > 0),
      switchMap((elements) => this.http.post(this._url + '/api/v2/orders/current', elements.map((el) => el.id)))
    ).subscribe((orders: BzApteka[]) => {
      orders.forEach((data: BzApteka) => {
        if (data.order.id !== null) {
          this.store.dispatch(SetOrderPharmaciesAction({ data }));
        }
      });
    });
  }

  public updateOrder(apteka: BzApteka): void {
    const order: IUpdateOrder = { id: null, statusId: null, rows: null };
    const temp: IProductRow[] = [];
    apteka.products.forEach((product: BzProduct) => {
      const productRow: IProductRow = {
        goodsId: product.id,
        price: product.price,
        priceShip: product.price,
        qty: product.orderedContentNum,
        qtyShip: product.orderedContentNum,
        priceSingle: product.priceSingle
      };
      temp.push(productRow);
    });
    order.id = apteka.order.id;
    order.statusId = apteka.order.statusId;
    order.rows = temp;
    this.http.put<BzApteka>(this._url + `/api/orders/update-status`, order).subscribe((response: BzApteka) => {
      this.store.dispatch(UpdateOrderAction({ pharm: response }));
    });
  }

  public cancelOrder(apteka: BzApteka): void {
    let params: HttpParams = new HttpParams();
    switch (apteka.order.statusId) {
      case '2.0':
        params = params.append('cancelType', '1');
        break;
      case '8.1':
        params = params.append('cancelType', '2');
        break;
      case '8.2':
        params = params.append('cancelType', '2');
        break;
      case '8.3':
        params = params.append('cancelType', '2');
        break;
      case '3.0':
        params = params.append('cancelType', '1');
        break;
      case '3.3':
        params = params.append('cancelType', '1');
        break;
      case '5.0':
        params = params.append('cancelType', '1');
        break;
      case '3.5':
        params = params.append('cancelType', '1');
        break;
      case '4.0':
        params = params.append('cancelType', '3');
        break;
    }

    this.http.put<BzApteka>(this._url + `/api/orders/cancel/${apteka.order.id}`, {}, { params }).subscribe((data: BzApteka) => {
      this.store.dispatch(CancelOneOrderAndArchiveAction({ apteka: data }));
    });
  }
}
